import { useState } from 'react';
import fechasBanner from '../../../../assets/fechas.webp';
import { TournamentRankingSection } from './TournamentScreens';
import { Panel } from './ExperienceLayout';

const PODIUM = [
  { label: '1er lugar', medal: '🥇', frame: 'border-[#F7E04B] bg-[#F7E04B]/10' },
  { label: '2do lugar', medal: '🥈', frame: 'border-[#E5E7EB]/70 bg-white/5' },
  { label: '3er lugar', medal: '🥉', frame: 'border-[#D08A4E]/80 bg-[#D08A4E]/10' },
];

// Primer, segundo y tercer lugar del ranking global, destacados como ganadores.
function WinnersPodium({ items }) {
  if (!items.length) return null;
  return (
    <section className="mt-5 text-center" aria-label="Ganadores del festival">
      <p className="text-[11px] font-black uppercase tracking-[.3em] text-[#F7E04B]">Ganadores</p>
      <div className="mt-3 space-y-2">
        {items.map((item, index) => (
          <div
            key={item.username}
            className={['flex items-center gap-3 border-2 px-3 py-3 text-left', PODIUM[index].frame].join(' ')}
          >
            <span className={index === 0 ? 'text-4xl' : 'text-3xl'} aria-hidden="true">{PODIUM[index].medal}</span>
            {/* El puntaje va debajo del nombre para que los usuarios largos quepan en una línea en el teléfono. */}
            <span className="min-w-0 flex-1">
              <small className="block text-[10px] font-black uppercase tracking-[.16em] text-[#F7E04B]">
                {PODIUM[index].label}
              </small>
              <strong className="block break-words font-display text-lg leading-tight text-white">{item.username}</strong>
              <span className="mt-1 block text-sm font-black text-[#F7E04B]">
                {item.totalScore.toLocaleString()} <small className="text-[9px] uppercase tracking-wider text-white">puntos</small>
              </span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function RankingScreen({ ranking, tournamentBoard, tournamentLoading, tournamentError, onBack }) {
  // El servidor es la única fuente del ranking oficial, incluso cuando está vacío.
  const official = ranking.loaded ? ranking.items : null;
  const seasonLabel = !ranking.season?.resetAt
    ? 'Desde el inicio'
    : `Temporada desde el ${new Date(ranking.season.resetAt).toLocaleDateString('es-MX', { dateStyle: 'medium' })}`;
  const [tab, setTab] = useState('global');

  const tabClass = (active) => ([
    'min-h-[44px] flex-1 border px-3 py-3 text-[10px] font-black uppercase tracking-[.14em] transition',
    active
      ? 'border-[#F7E04B] bg-[#F7E04B] text-black'
      : 'border-[#3a3344] text-[#a99ab4] hover:text-white',
  ].join(' '));

  return (
    <Panel wide card>
      <img
        src={fechasBanner}
        alt="Ñaca Ñaca 3ra Edición — Festival Internacional de Cine de Terror, del 02 al 04 de octubre"
        className="mx-auto h-auto w-[min(64vw,200px)] object-contain"
      />
      <h1 className="mt-7 font-display text-4xl uppercase leading-none text-[#F7E04B]">Ranking</h1>
      <div className="mt-5 flex gap-2" role="tablist" aria-label="Rankings">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'global'}
          onClick={() => setTab('global')}
          className={tabClass(tab === 'global')}
        >
          Global
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'torneo'}
          onClick={() => setTab('torneo')}
          className={tabClass(tab === 'torneo')}
        >
          Torneo
        </button>
      </div>

      {tab === 'global' ? (
        <div>
          <WinnersPodium items={official ? official.slice(0, PODIUM.length) : []} />
          <div className="mt-6 flex items-baseline justify-between gap-3 border-b-2 border-[#7C3AED] pb-3">
            <span className="text-base font-normal uppercase tracking-normal text-white">Puntos totales · Top 100</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#C9A8FF]">{seasonLabel}</span>
          </div>
          <div className="mt-3 text-left">
            {ranking.isLoading && !official ? (
              <p className="py-10 text-center text-sm text-white">Cargando ranking…</p>
            ) : official?.length ? (
              // Los tres primeros ya aparecen arriba como ganadores.
              official.slice(PODIUM.length, 100).map((item, index) => (
                <div key={item.username} className="flex items-center gap-3 border-b border-white/20 py-4">
                  <span className="w-12 shrink-0 font-display text-2xl text-white">
                    {String(index + 1 + PODIUM.length).padStart(2, '0')}
                  </span>
                  <span className="flex-1 font-bold text-white">
                    {item.username}
                    <small className="ml-2 text-[10px] uppercase tracking-wider text-white">{item.zones} zonas maldecidas</small>
                  </span>
                  <span className="text-right">
                    <small className="block text-[9px] font-black uppercase tracking-wider text-white">Total</small>
                    <strong className="font-black text-[#F7E04B]">{item.totalScore.toLocaleString()}</strong>
                  </span>
                </div>
              ))
            ) : (
              <p className="py-10 text-center text-sm text-white">Aún no hay registros de puntos acumulados.</p>
            )}
          </div>
          {ranking.error && !official && (
            <p className="mt-3 text-center text-[11px] text-[#C9A8FF]" role="status">
              No se pudo consultar el ranking oficial. Inténtalo de nuevo.
            </p>
          )}
        </div>
      ) : (
        <TournamentRankingSection
          board={tournamentBoard}
          isLoading={tournamentLoading}
          error={tournamentError}
        />
      )}

      <button
        onClick={onBack}
        className={[
          'mt-7 w-full rounded-sm border border-[#C9A8FF] bg-[#7C3AED] px-5 py-4 font-black uppercase',
          'tracking-[.12em] text-white shadow-[4px_4px_0_#0A1A0F] transition-colors',
          'hover:bg-[#6D2EB5]',
        ].join(' ')}
      >
        Volver al mapa
      </button>
    </Panel>
  );
}
